<template>
  <div class="soar-container flex flex-col gap-y-4">
    <div class="w-full flex gap-x-4">
      <button @click="$router.back()">
        <faChevronLeft class="w-4 fill-primary" />
      </button>
      <h1
        class="flex-1 text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300"
      >
        {{ $t('RegisterEstablishmentSubUser') }}
      </h1>
    </div>
    <div class="infobar p-2 pb-1 rounded-md border-sky-500 bg-sky-400 text-white text-sm">
      {{ $t('registerEstablishmentSubUserHint') }}
    </div>

    <Form
      v-if="!isLoading"
      :initial-values="initialValues"
      v-slot="{ values, setFieldValue, setFieldError }"
      class="flex flex-col gap-3"
      @submit="(values, context) => onSubmit(values, context)"
    >
      <div class="flex gap-4 flex-col md:flex-row">
        <Field :rules="requiredRule" name="englishFirstName" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('EnglishFirstName')"
            label-class="w-full"
            class="w-full md:w-1/4"
            direction="flex-col"
            @on-blur="translateField(field.value, setFieldValue, 'arabicFirstName')"
          />
        </Field>
        <Field name="englishSecondName" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('EnglishSecondName')"
            :is-required="false"
            label-class="w-full"
            class="w-full md:w-1/4"
            direction="flex-col"
            @on-blur="translateField(field.value, setFieldValue, 'arabicSecondName')"
          />
        </Field>
        <Field name="englishThirdName" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('EnglishThirdName')"
            :is-required="false"
            label-class="w-full"
            class="w-full md:w-1/4"
            direction="flex-col"
            @on-blur="translateField(field.value, setFieldValue, 'arabicThirdName')"
          />
        </Field>
        <Field :rules="requiredRule" name="englishFamilyName" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('EnglishFamilyName')"
            label-class="w-full"
            class="w-full md:w-1/4"
            direction="flex-col"
            @on-blur="translateField(field.value, setFieldValue, 'arabicFamilyName')"
          />
        </Field>
      </div>
      <div class="flex gap-4 flex-col md:flex-row">
        <Field :rules="requiredRule" name="arabicFirstName" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('ArabicFirstName')"
            label-class="w-full"
            class="w-full md:w-1/4"
            direction="flex-col"
          />
        </Field>
        <Field name="arabicSecondName" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('ArabicSecondName')"
            :is-required="false"
            label-class="w-full"
            class="w-full md:w-1/4"
            direction="flex-col"
          />
        </Field>
        <Field name="arabicThirdName" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('ArabicThirdName')"
            :is-required="false"
            label-class="w-full"
            class="w-full md:w-1/4"
            direction="flex-col"
          />
        </Field>
        <Field :rules="requiredRule" name="arabicFamilyName" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('ArabicFamilyName')"
            label-class="w-full"
            class="w-full md:w-1/4"
            direction="flex-col"
          />
        </Field>
      </div>
      <div class="flex gap-4 flex-col md:flex-row">
        <Field :rules="requiredRule" name="identityNumber" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('IdentityNumber')"
            mask="784-####-#######-#"
            input-class="px-3 py-1.5"
            label-class="w-full"
            class="w-full md:w-1/2"
            direction="flex-col"
          />
        </Field>
        <Field
          :rules="requiredRule"
          name="dateOfBirth"
          v-slot="{ field, errors: errs, handleChange }"
        >
          <Datepicker
            :field="field"
            :errors="errs"
            :label="$t('DateOfBirth')"
            :default-value="field.value"
            input-class="ps-3 py-0.5"
            class="w-full md:w-1/2"
            max-date="__now__"
            @on-change="(date) => handleChange(date)"
          >
          </Datepicker>
        </Field>
      </div>
      <div class="flex gap-4 flex-col md:flex-row">
        <Field :rules="requiredRule" name="genderId" v-slot="{ field, errors: errs, handleChange }">
          <Dropdown
            :field="field"
            :errors="errs"
            :label="$t('Gender')"
            :options="genders"
            class="w-full md:w-1/2"
            direction="flex-col"
            @on-selection="(item) => handleChange(+item?.code)"
          ></Dropdown>
        </Field>
        <Field
          :rules="requiredRule"
          name="nationalityID"
          v-slot="{ field, errors: errs, handleChange }"
        >
          <Dropdown
            :field="field"
            :errors="errs"
            :label="$t('Nationality')"
            :options="countries"
            input-class="px-3 py-1.5"
            class="w-full md:w-1/2"
            direction="flex-col"
            @on-selection="(item) => handleChange(+item?.code)"
          ></Dropdown>
        </Field>
      </div>
      <div class="flex gap-4 flex-col md:flex-row">
        <Field
          :rules="requiredRule"
          name="residenceCountryId"
          v-slot="{ field, errors: errs, handleChange }"
        >
          <Dropdown
            :field="field"
            :errors="errs"
            :label="$t('ResidenceCountry')"
            :options="countries"
            input-class="px-3 py-1.5"
            class="w-full md:w-1/2"
            direction="flex-col"
            @on-selection="(item) => handleChange(+item?.code)"
          ></Dropdown>
        </Field>
        <Field
          :rules="requiredRule"
          name="mobileNumber"
          v-slot="{ field, errors: errs, handleChange }"
        >
          <ComboText
            :field="field"
            :errors="errs"
            :label="$t('MobileNo')"
            :options="countries"
            input-class="px-3 py-1.5"
            class="w-full md:w-1/2"
            direction="flex-col"
            @on-selection="(phone) => handleChange(phone)"
          ></ComboText>
        </Field>
      </div>
      <div class="flex gap-4 flex-col md:flex-row">
        <Field name="userProfileActivationToken" v-slot="{ field, errors: errs }">
          <TextInput
            :field="field"
            :errors="errs"
            :label="$t('verificationTokenRequiredMessage')"
            placeholder=" "
            is-read-only
            input-class="px-3 py-1.5 font-mono !text-xs"
            label-class="w-full"
            class="w-full md:w-1/2"
            direction="flex-col"
          >
            <template #suffix>
              <div class="max-w-max w-full flex items-stretch">
                <button
                  v-if="values.userProfileActivationToken"
                  class="group max-w-max w-full flex items-stretch read-only:bg-brand-gray-250 disabled:bg-brand-gray-600/80 disabled:cursor-not-allowed"
                  @click.prevent="copyValidationCode(values.userProfileActivationToken)"
                >
                  <div class="p-2">
                    <faCopy class="w-4 fill-primary/80 group-hover:fill-primary"></faCopy>
                  </div>
                </button>
                <button
                  :disabled="!values.identityNumber || !values.nationalityID || !values.dateOfBirth"
                  class="group max-w-max w-full flex items-stretch gap-x-2 ltr:font-sans text-xs bg-primary/80 hover:bg-primary text-white disabled:bg-brand-gray-600/80 disabled:cursor-not-allowed"
                  @click.prevent="generateVerificationToken(values, setFieldValue, setFieldError)"
                >
                  <div class="p-2 bg-primary group-disabled:bg-brand-gray-600">
                    <faKey class="w-4 fill-white"></faKey>
                  </div>
                  <span class="w-full flex items-center text-center">
                    {{ $t('GenerateToken') }}
                  </span>
                  <div class="p-0.5"></div>
                </button>
              </div>
            </template>
          </TextInput>
        </Field>
        <EmailVerification class="w-full md:w-1/2" @on-otp="(otp) => setFieldValue('otp', otp)" />
      </div>
      <div class="w-full flex justify-end">
        <button
          class="min-w-28 text-center rounded-md bg-primary/80 pe-2 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
        >
          <div class="p-2 bg-primary rounded-s-md">
            <faDiskette class="w-4 fill-white"></faDiskette>
          </div>
          <span class="w-full text-center">
            {{ $t('Save') }}
          </span>
        </button>
      </div>
    </Form>
  </div>
</template>

<script setup>
import faChevronLeft from '@/assets/icons/faChevronLeft.svg?component'
import faCopy from '@/assets/icons/faCopy.svg?component'
import faDiskette from '@/assets/icons/faDiskette.svg?component'
import faKey from '@/assets/icons/faKey.svg?component'
import ComboText from '@/components/SimpleForm/ComboText.vue'
import Datepicker from '@/components/SimpleForm/Datepicker.vue'
import Dropdown from '@/components/SimpleForm/Dropdown.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import axiosInstance from '@/services'
import { useLookupsStore } from '@/stores/lookups'
import EmailVerification from '@/views/establishment/user-management/new-user/EmailVerification.vue'
import { notify } from 'notiwind'
import { Field, Form } from 'vee-validate'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()

//#region rules
function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

const lookupsStore = useLookupsStore()

const isLoading = ref(true)

const genders = ref()
const religions = ref()
const maritalStatuses = ref()
const countries = ref()
const languages = ref()
const timeZones = ref()

// const initialValues = {
//   email: 'asdf@asdf.com',
//   englishFirstName: 'Kevin',
//   englishFamilyName: 'Espaldon',
//   arabicFirstName: 'كيفين',
//   arabicFamilyName: 'ايسبالدون',
//   identityNumber: '784199010725432',
//   dateOfBirth: '03/07/1990',
//   genderId: 2,
//   nationalityID: 19,
//   residenceCountryId: 19,
//   mobileNumber: '00971524132422',
//   userProfileActivationToken: null,
//   englishSecondName: 'Angelo',
//   englishThirdName: 'Apostol',
//   arabicSecondName: 'انجيلو',
//   arabicThirdName: 'ابوستول'
// }

onMounted(async () => {
  genders.value = await lookupsStore.fetchLookup('/api/lookup/genders')
  religions.value = await lookupsStore.fetchLookup('/api/lookup/religions')
  maritalStatuses.value = await lookupsStore.fetchLookup('/api/lookup/maritalStatuses')
  countries.value = await lookupsStore.fetchLookup('/api/lookup/countries')
  languages.value = await lookupsStore.fetchLookup('/api/lookup/GetUsedLanguages')
  timeZones.value = await lookupsStore.fetchLookup('/api/lookup/timeZones')

  isLoading.value = false
})

const copyValidationCode = (validationCode) => {
  console.log('TODO: copyValidationCode', validationCode)
}

const generateVerificationToken = async (form, setFieldValue, setFieldError) => {
  try {
    const response = await axiosInstance.post('/api/userProfile/generateActivationToken', form)
    setFieldValue('userProfileActivationToken', response.activationToken)
  } catch (error) {
    setFieldError('userProfileActivationToken', 'INVALID_REQUIRED')
  }
}

const onSubmit = async (form, context) => {
  let isValid = true
  if (!form?.activationToken) {
    context.setFieldError('userProfileActivationToken', 'accountVerificationTokenIsMissing')
    isValid = false
  }

  if (!form?.otp) {
    context.setFieldError('email', 'MissingOtpEmail')
    isValid = false
  }

  if (!isValid) return

  await axiosInstance.post('/api/userProfile/registerSubUser', form)
  notify(
    {
      group: 'success',
      title: i18n.t('addedSuccessfully')
    },
    6000
  )
}

const translateField = async (source, setFieldValue, destination) => {
  const response = await axiosInstance.get(`/api/translation/${source}`)
  setFieldValue(destination, JSON.parse(response)[source.toLowerCase()])
}
</script>

<style lang="scss" scoped></style>
